import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../HsePageChrome";
import { ModuleFilter } from "../../../utils/ModuleFilter";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { evaluationApiError, exportEvaluationPdf, fetchEvaluations } from "../../../../api/hse/evaluations";
import { HseRowActionButton, HseRowActionLink, HseRowActions } from "../HseRowActions";
import { EvaluationLabelInfo } from "./EvaluationLabelInfo";
import {
  EVALUATION_STATUSES,
  EVALUATION_TYPES,
  evaluationPath,
  formatDisplayDate,
  personLabel,
  resultTone,
  statusTone,
} from "./evaluationUtils";

export function EvaluationListPage({ evaluationType }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAudit = evaluationType === EVALUATION_TYPES.INTERNAL_AUDIT;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportingId, setExportingId] = useState(null);
  const [filters, setFilters] = useState({ query: "", evaluation_statuses: [] });

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchEvaluations({
        kind: evaluationType,
        query: filters.query,
        status: filters.evaluation_statuses?.[0],
      });
      setItems(response.data || []);
    } catch (err) {
      setError(evaluationApiError(err) || t("hse.evaluation.list.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationType, filters.query, filters.evaluation_statuses]);

  const newPath = evaluationPath({ kind: evaluationType }, "new");
  const TypeIcon = isAudit ? ClipboardDocumentCheckIcon : UserGroupIcon;

  const handleExportPdf = async (item, event) => {
    event?.stopPropagation();
    if (!item?.id || exportingId) return;
    try {
      setExportingId(item.id);
      await exportEvaluationPdf(item.id, `hse-evaluation-${item.id}.pdf`);
    } catch (err) {
      toast.error(evaluationApiError(err) || t("hse.evaluation.export_failed"));
    } finally {
      setExportingId(null);
    }
  };

  return (
    <HsePageChrome
      variant="list"
      title={t(isAudit ? "hse.evaluation.list.title_audits" : "hse.evaluation.list.title_reviews")}
      subtitle={t(isAudit ? "hse.evaluation.list.subtitle_audits" : "hse.evaluation.list.subtitle_reviews")}
      listCount={loading ? null : items.length}
      mainButton={{
        label: t(isAudit ? "hse.evaluation.list.new_audit" : "hse.evaluation.list.new_review"),
        icon: PlusIcon,
        path: newPath,
      }}
    >
      <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-4 text-sm font-semibold text-slate-700">
          <EvaluationLabelInfo
            label={t(`hse.evaluation.types.${evaluationType}`)}
            infoKey={isAudit ? "internal_audit" : "management_review"}
          />
        </div>

        <ModuleFilter
          filterCard
          hideStatusFilter
          searchPlaceholder={t("hse.evaluation.list.search")}
          initialFilters={filters}
          onFiltersChange={(next) =>
            setFilters({
              query: next.query || "",
              evaluation_statuses: next.evaluation_statuses || [],
            })
          }
          customDropdownFilters={[
            {
              key: "evaluation_statuses",
              label: t("hse.evaluation.fields.status"),
              options: EVALUATION_STATUSES.map((status) => ({
                value: status,
                label: t(`hse.evaluation.statuses.${status}`),
                class: "bg-slate-100 text-slate-800",
              })),
            },
          ]}
        />

        <div className="mt-6 overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
          {loading ? (
            <div className="p-8">
              <ResourceLoading size="base" />
            </div>
          ) : error ? (
            <ResourceError errors={[error]} onRetry={load} />
          ) : items.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <TypeIcon className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-600">{t("hse.evaluation.list.empty")}</p>
              <button
                type="button"
                onClick={() => navigate(newPath)}
                className="mt-4 rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
              >
                {t(isAudit ? "hse.evaluation.list.new_audit" : "hse.evaluation.list.new_review")}
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="cursor-pointer px-5 py-4 hover:bg-slate-50 sm:px-6"
                  onClick={() => navigate(evaluationPath(item))}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-bold text-slate-900">{item.title}</p>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusTone(
                            item.status
                          )}`}
                        >
                          {t(`hse.evaluation.statuses.${item.status}`)}
                        </span>
                        {item.result ? (
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${resultTone(
                              item.result
                            )}`}
                          >
                            {t(`hse.evaluation.results.${item.result}`)}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {[
                          formatDisplayDate(item.held_on || item.scheduled_on),
                          personLabel(item.responsible_user || item.lead),
                          item.open_tiltak_count
                            ? t("hse.evaluation.tiltak.open_count", { count: item.open_tiltak_count })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <HseRowActions>
                      <HseRowActionLink to={evaluationPath(item)} icon={EyeIcon}>
                        {t("hse.evaluation.view.open")}
                      </HseRowActionLink>
                      <HseRowActionLink to={evaluationPath(item, "edit")} icon={PencilIcon}>
                        {t("hse.evaluation.view.edit")}
                      </HseRowActionLink>
                      <HseRowActionButton
                        icon={ArrowDownTrayIcon}
                        disabled={exportingId === item.id}
                        onClick={(event) => handleExportPdf(item, event)}
                      >
                        {exportingId === item.id
                          ? t("hse.evaluation.exporting")
                          : t("hse.evaluation.export_pdf")}
                      </HseRowActionButton>
                    </HseRowActions>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </HsePageChrome>
  );
}
